"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var luxon_1 = require("luxon");
var useLocalisation_1 = require("../../hooks/useLocalisation");
var API_DATE_FORMAT = "yyyy-MM-dd";
var API_TIME_FORMAT = "HH:mm:ss.SSS";
var TIME_ZONE_FULL_NAME_FORMAT = "ZZZZZ";
var TIME_ZONE_SHORT_NAME_FORMAT = "ZZZZ";
var DateAndTimeStore = /** @class */ (function () {
    function DateAndTimeStore() {
        this.dateTimeUTC = luxon_1.DateTime.utc();
    }
    DateAndTimeStore.getInstance = function () {
        if (DateAndTimeStore.myInstance === null) {
            DateAndTimeStore.myInstance = new DateAndTimeStore();
        }
        return this.myInstance;
    };
    DateAndTimeStore.prototype.getCurrentTimezone = function (language) {
        if (!language) {
            language = useLocalisation_1.getBrowserLanguage();
        }
        return {
            timezone: this.dateTimeLocal.zoneName,
            timezoneName: this.dateTimeLocal.offsetNameLong,
            timezoneLocalizedName: this.dateTimeLocal.setLocale(language).offsetNameLong,
            timezoneShortName: this.dateTimeLocal.offsetNameShort,
        };
    };
    DateAndTimeStore.prototype.init = function (timezone) {
        this.timezone = timezone;
        this.dateTimeLocal = this.dateTimeUTC.setZone(this.timezone);
    };
    DateAndTimeStore.prototype.changeTimezone = function (timezone) {
        this.timezone = timezone;
        this.dateTimeLocal = this.dateTimeUTC.setZone(this.timezone);
    };
    DateAndTimeStore.prototype.getUTCTimestamp = function () {
        this.cryIfNotInitialised();
        return this.dateTimeUTC.toISO();
    };
    Object.defineProperty(DateAndTimeStore.prototype, "localDateTime", {
        get: function () {
            return this.dateTimeLocal;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "utcDateTime", {
        get: function () {
            return this.dateTimeUTC;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "oneMonthAgo", {
        get: function () {
            return luxon_1.DateTime.utc()
                .minus({ months: 1 })
                .startOf("day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "todayStart", {
        get: function () {
            return luxon_1.DateTime.utc().startOf("day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "todayEnd", {
        get: function () {
            return luxon_1.DateTime.utc().endOf("day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "yesterdayStart", {
        get: function () {
            return luxon_1.DateTime.utc()
                .minus({ days: 1 })
                .startOf("day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "yesterdayEnd", {
        get: function () {
            return luxon_1.DateTime.utc()
                .minus({ days: 1 })
                .endOf("day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "lastMonth", {
        get: function () {
            return luxon_1.DateTime.utc()
                .minus({ months: 1 })
                .startOf("day");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "dateFormat", {
        get: function () {
            return API_DATE_FORMAT;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateAndTimeStore.prototype, "timeFormat", {
        get: function () {
            return API_TIME_FORMAT;
        },
        enumerable: true,
        configurable: true
    });
    DateAndTimeStore.prototype.getDateForApiWithTimezoneOffset = function (date) {
        this.cryIfNotInitialised();
        var updated = date.setZone(this.timezone, { keepLocalTime: true });
        var flipped = updated.setZone("UTC", {
            keepLocalTime: this.timezone != "UTC" ? false : true,
        });
        var dateFormat = flipped.toFormat(this.dateFormat);
        var timeFormat = flipped.toFormat(this.timeFormat);
        return dateFormat + "T" + timeFormat + "Z";
    };
    DateAndTimeStore.prototype.getEndDateForApiWithTimezoneOffset = function (date) {
        this.cryIfNotInitialised();
        var updated = date.setZone(this.timezone, { keepLocalTime: true });
        if (date.hour === 23 && date.minute === 59) {
            updated = updated.plus({ minutes: 1 });
        }
        var flipped = updated.setZone("UTC", {
            keepLocalTime: this.timezone != "UTC" ? false : true,
        });
        var dateFormat = flipped.toFormat(this.dateFormat);
        var timeFormat = flipped.toFormat(this.timeFormat);
        return dateFormat + "T" + timeFormat + "Z";
    };
    DateAndTimeStore.prototype.getTimestampForTimezoneFromTimestamp = function (timestamp) {
        var dateFromTimestamp = luxon_1.DateTime.fromISO(timestamp).setZone("utc");
        return this.getTimestampForTimezone(dateFromTimestamp);
    };
    DateAndTimeStore.prototype.getTimestampForTimezoneFromEpochSeconds = function (epochSeconds) {
        var dateFromEpoch = luxon_1.DateTime.fromMillis(epochSeconds * 1000).setZone("utc");
        return this.getTimestampForTimezone(dateFromEpoch);
    };
    DateAndTimeStore.prototype.getDateTimeForTimezoneFromEpochSeconds = function (epochSeconds) {
        var dateFromEpoch = luxon_1.DateTime.fromMillis(epochSeconds * 1000).setZone("utc");
        return this.getDateTimeForTimezone(dateFromEpoch);
    };
    DateAndTimeStore.prototype.getDateTimeForTimezoneFromTimestamp = function (timestamp) {
        var dateFromTimestamp = luxon_1.DateTime.fromISO(timestamp).setZone(this.timezone);
        return this.getDateTimeForTimezone(dateFromTimestamp);
    };
    DateAndTimeStore.prototype.getUTCDateTimeFromOffsetTimestamp = function (timestamp) {
        var utcDateFromTimestamp = luxon_1.DateTime.fromISO(timestamp).setZone("utc");
        var local = utcDateFromTimestamp.setZone(this.timezone).offset / 60;
        return utcDateFromTimestamp.minus({ hours: local });
    };
    DateAndTimeStore.prototype.getDateTimeLocalTimezoneLocalizedName = function (language) {
        if (!language) {
            language = useLocalisation_1.getBrowserLanguage();
        }
        var dt = this.dateTimeLocal.setLocale(language);
        var timeZone = dt.isValid ? dt.toFormat(TIME_ZONE_FULL_NAME_FORMAT) : "";
        if (timeZone && timeZone === "UTC" && language === "en") {
            timeZone = "Coordinated Universal Time";
        }
        return timeZone;
    };
    DateAndTimeStore.prototype.getDateTimeLocalTimezoneLocalizedShortName = function (language) {
        if (!language) {
            language = useLocalisation_1.getBrowserLanguage();
        }
        var dt = this.dateTimeLocal.setLocale(language);
        return dt.isValid ? dt.toFormat(TIME_ZONE_SHORT_NAME_FORMAT) : "";
    };
    DateAndTimeStore.prototype.getDateTimeForTimezone = function (date) {
        return date.setZone(this.timezone);
    };
    DateAndTimeStore.prototype.getTimestampForTimezone = function (date) {
        var localDate = date.setZone(this.timezone);
        var dateFormat = localDate.toFormat(this.dateFormat);
        var timeFormat = localDate.toFormat(this.timeFormat);
        return dateFormat + "T" + timeFormat + "Z";
    };
    DateAndTimeStore.prototype.cryIfNotInitialised = function () {
        if (!this.timezone) {
            throw new Error("DateAndTimeStore not initialsed. Please call initialise() with a timezone first.");
        }
    };
    DateAndTimeStore.myInstance = null;
    return DateAndTimeStore;
}());
exports.DateAndTimeStore = DateAndTimeStore;
